/**
 * Race condition prevention utilities for form submissions and data updates
 */

/**
 * Debounce function to prevent rapid successive calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function to limit execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Request deduplication to prevent duplicate API calls
 */
class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map();
  }

  /**
   * Execute request only if not already pending
   * @param {string} key - Unique request key
   * @param {Function} requestFn - Request function
   * @returns {Promise} - Request result
   */
  async execute(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = requestFn();
    this.pendingRequests.set(key, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Check if request is pending
   * @param {string} key - Request key
   * @returns {boolean} - True if pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Cancel pending request
   * @param {string} key - Request key
   */
  cancel(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Clear all pending requests
   */
  clear() {
    this.pendingRequests.clear();
  }
}

/**
 * Form state synchronization to prevent conflicts
 */
class FormStateSync {
  constructor() {
    this.activeForms = new Map();
    this.formLocks = new Map();
  }

  /**
   * Acquire form lock
   * @param {string} formId - Form identifier
   * @param {number} timeout - Lock timeout in milliseconds
   * @returns {boolean} - True if lock acquired
   */
  acquireLock(formId, timeout = 30000) {
    const now = Date.now();
    const existingLock = this.formLocks.get(formId);

    if (existingLock && now - existingLock.timestamp < timeout) {
      return false; // Lock already active
    }

    this.formLocks.set(formId, { timestamp: now, timeout });
    return true;
  }

  /**
   * Release form lock
   * @param {string} formId - Form identifier
   */
  releaseLock(formId) {
    this.formLocks.delete(formId);
  }

  /**
   * Check if form is locked
   * @param {string} formId - Form identifier
   * @returns {boolean} - True if locked
   */
  isLocked(formId) {
    const lock = this.formLocks.get(formId);
    if (!lock) return false;

    const now = Date.now();
    if (now - lock.timestamp > lock.timeout) {
      this.formLocks.delete(formId);
      return false;
    }

    return true;
  }

  /**
   * Register active form
   * @param {string} formId - Form identifier
   * @param {Object} formData - Form data
   */
  registerForm(formId, formData) {
    this.activeForms.set(formId, {
      data: formData,
      timestamp: Date.now(),
      lastModified: Date.now()
    });
  }

  /**
   * Update form data
   * @param {string} formId - Form identifier
   * @param {Object} formData - Updated form data
   */
  updateForm(formId, formData) {
    const existing = this.activeForms.get(formId);
    if (existing) {
      existing.data = formData;
      existing.lastModified = Date.now();
    }
  }

  /**
   * Get form data
   * @param {string} formId - Form identifier
   * @returns {Object|null} - Form data or null
   */
  getForm(formId) {
    const form = this.activeForms.get(formId);
    return form ? form.data : null;
  }

  /**
   * Unregister form
   * @param {string} formId - Form identifier
   */
  unregisterForm(formId) {
    this.activeForms.delete(formId);
    this.formLocks.delete(formId);
  }

  /**
   * Clean up expired forms
   * @param {number} maxAge - Maximum age in milliseconds
   */
  cleanup(maxAge = 3600000) { // 1 hour default
    const now = Date.now();
    for (const [formId, form] of this.activeForms.entries()) {
      if (now - form.timestamp > maxAge) {
        this.unregisterForm(formId);
      }
    }
  }
}

/**
 * Optimistic updates with rollback capability
 */
class OptimisticUpdater {
  constructor() {
    this.pendingUpdates = new Map();
    this.updateHistory = new Map();
  }

  /**
   * Apply optimistic update
   * @param {string} key - Update key
   * @param {Object} currentState - Current state
   * @param {Function} updateFn - Update function
   * @param {Function} rollbackFn - Rollback function
   * @returns {Object} - Updated state
   */
  applyUpdate(key, currentState, updateFn, rollbackFn) {
    // Store current state for potential rollback
    this.updateHistory.set(key, {
      state: currentState,
      timestamp: Date.now(),
      rollbackFn
    });

    // Apply optimistic update
    const updatedState = updateFn(currentState);
    
    // Mark as pending
    this.pendingUpdates.set(key, {
      state: updatedState,
      timestamp: Date.now()
    });

    return updatedState;
  }

  /**
   * Confirm update was successful
   * @param {string} key - Update key
   */
  confirmUpdate(key) {
    this.pendingUpdates.delete(key);
    this.updateHistory.delete(key);
  }

  /**
   * Rollback failed update
   * @param {string} key - Update key
   * @returns {Object|null} - Previous state or null
   */
  rollbackUpdate(key) {
    const history = this.updateHistory.get(key);
    if (history && history.rollbackFn) {
      const previousState = history.rollbackFn();
      this.pendingUpdates.delete(key);
      this.updateHistory.delete(key);
      return previousState;
    }
    return null;
  }

  /**
   * Get pending updates
   * @returns {Array} - Array of pending update keys
   */
  getPendingUpdates() {
    return Array.from(this.pendingUpdates.keys());
  }

  /**
   * Check if update is pending
   * @param {string} key - Update key
   * @returns {boolean} - True if pending
   */
  isPending(key) {
    return this.pendingUpdates.has(key);
  }
}

/**
 * Concurrent operation manager
 */
class ConcurrentOperationManager {
  constructor() {
    this.operations = new Map();
    this.requestDeduplicator = new RequestDeduplicator();
    this.formStateSync = new FormStateSync();
    this.optimisticUpdater = new OptimisticUpdater();
  }

  /**
   * Execute operation with race condition prevention
   * @param {string} operationId - Unique operation identifier
   * @param {Function} operation - Operation function
   * @param {Object} options - Operation options
   * @returns {Promise} - Operation result
   */
  async executeOperation(operationId, operation, options = {}) {
    const {
      deduplicate = true,
      lockForm = false,
      optimistic = false,
      timeout = 30000
    } = options;

    // Check if operation is already running
    if (this.operations.has(operationId)) {
      throw new Error(`Operation ${operationId} is already in progress`);
    }

    // Acquire form lock if requested
    if (lockForm && !this.formStateSync.acquireLock(operationId, timeout)) {
      throw new Error(`Form is locked for operation ${operationId}`);
    }

    try {
      // Execute with deduplication if requested
      if (deduplicate) {
        return await this.requestDeduplicator.execute(operationId, operation);
      } else {
        const promise = operation();
        this.operations.set(operationId, promise);
        
        try {
          const result = await promise;
          return result;
        } finally {
          this.operations.delete(operationId);
        }
      }
    } finally {
      // Release form lock if acquired
      if (lockForm) {
        this.formStateSync.releaseLock(operationId);
      }
    }
  }

  /**
   * Apply optimistic update
   * @param {string} updateId - Update identifier
   * @param {Object} currentState - Current state
   * @param {Function} updateFn - Update function
   * @param {Function} rollbackFn - Rollback function
   * @returns {Object} - Updated state
   */
  applyOptimisticUpdate(updateId, currentState, updateFn, rollbackFn) {
    return this.optimisticUpdater.applyUpdate(updateId, currentState, updateFn, rollbackFn);
  }

  /**
   * Confirm optimistic update
   * @param {string} updateId - Update identifier
   */
  confirmOptimisticUpdate(updateId) {
    this.optimisticUpdater.confirmUpdate(updateId);
  }

  /**
   * Rollback optimistic update
   * @param {string} updateId - Update identifier
   * @returns {Object|null} - Previous state
   */
  rollbackOptimisticUpdate(updateId) {
    return this.optimisticUpdater.rollbackUpdate(updateId);
  }

  /**
   * Check if operation is pending
   * @param {string} operationId - Operation identifier
   * @returns {boolean} - True if pending
   */
  isOperationPending(operationId) {
    return this.operations.has(operationId) || 
           this.requestDeduplicator.isPending(operationId) ||
           this.optimisticUpdater.isPending(operationId);
  }

  /**
   * Cancel operation
   * @param {string} operationId - Operation identifier
   */
  cancelOperation(operationId) {
    this.operations.delete(operationId);
    this.requestDeduplicator.cancel(operationId);
    this.formStateSync.releaseLock(operationId);
  }

  /**
   * Clean up expired operations and data
   */
  cleanup() {
    this.formStateSync.cleanup();
    
    // Clean up old operations
    const now = Date.now();
    for (const [operationId, operation] of this.operations.entries()) {
      if (now - operation.timestamp > 300000) { // 5 minutes
        this.operations.delete(operationId);
      }
    }
  }
}

// Global instances
export const requestDeduplicator = new RequestDeduplicator();
export const formStateSync = new FormStateSync();
export const optimisticUpdater = new OptimisticUpdater();
export const concurrentOperationManager = new ConcurrentOperationManager();

// Cleanup interval
setInterval(() => {
  concurrentOperationManager.cleanup();
}, 60000); // Every minute

// Export utility functions
export {
  RequestDeduplicator,
  FormStateSync,
  OptimisticUpdater,
  ConcurrentOperationManager
}; 